// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect, useRef } from 'react';
import { X, Fish, Ruler, MapPin, Calendar, Scale, MessageSquare, Image as ImageIcon, Upload } from 'lucide-react';
import { fetchWVStreams } from '../utils/streamService';

const PostCatchModal = ({ isOpen, onClose, onSubmit, user }) => {
  const [content, setContent] = useState('');
  const [species, setSpecies] = useState('');
  const [length, setLength] = useState('');
  const [weight, setWeight] = useState('');
  const [location, setLocation] = useState('');
  const [dateCaught, setDateCaught] = useState('');
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      loadStreams();
      const today = new Date().toISOString().split('T')[0];
      setDateCaught(today);
    }
  }, [isOpen]);

  const loadStreams = async () => {
    try {
      const streamData = await fetchWVStreams();
      setStreams(streamData);
    } catch (error) {
      console.error('Error loading streams:', error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError(null);

    const postData = {
      content: content.trim(),
      species: species.trim() || null,
      length: length.trim() || null,
      weight: weight.trim() || null,
      location: location.trim() || null,
      dateCaught: dateCaught || null,
      imageFile: imageFile || null
    };

    try {
      await onSubmit(postData);
      // Reset form only on success
      setContent('');
      setSpecies('');
      setLength('');
      setWeight('');
      setLocation('');
      setDateCaught('');
      setImageFile(null);
      setImagePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setSubmitError(err?.message || 'Failed to post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header with gradient */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Share Your Catch</h2>
            <p className="modal-subtitle">Tell the community about your latest fishing adventure</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="modal-form">
          {/* Image Upload Section */}
          <div className="form-section">
            <div className="form-section-header">
              <ImageIcon size={20} />
              <span>Photo</span>
            </div>

            {!imagePreview ? (
              <div 
                className="image-upload-area"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={32} />
                <p className="upload-text">Click to upload a photo</p>
                <p className="upload-subtext">PNG, JPG up to 10MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
              </div>
            ) : (
              <div className="image-preview-container">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={removeImage}
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Story Section */}
          <div className="form-section">
            <div className="form-section-header">
              <MessageSquare size={20} />
              <span>Your Story</span>
            </div>
            <textarea
              className="catch-textarea"
              placeholder="Describe your fishing experience... Where were you? What was the weather like? Any tips to share?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              rows={4}
            />
          </div>

          {/* Catch Details Section */}
          <div className="form-section">
            <div className="form-section-header">
              <Fish size={20} />
              <span>Catch Details</span>
            </div>

            <div className="form-grid-2">
              {/* Species */}
              <div className="input-with-icon">
                <Fish size={18} className="input-icon" />
                <select
                  className="modern-select"
                  value={species}
                  onChange={(e) => setSpecies(e.target.value)}
                >
                  <option value="">Select Species</option>
                  <option value="Brook Trout">Brook Trout</option>
                  <option value="Brown Trout">Brown Trout</option>
                  <option value="Rainbow Trout">Rainbow Trout</option>
                  <option value="Golden Trout">Golden Trout</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Length */}
              <div className="input-with-icon">
                <Ruler size={18} className="input-icon" />
                <input
                  type="text"
                  className="modern-input"
                  placeholder='Length (e.g., 14")'
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                />
              </div>
            </div>

            {/* Weight */}
            <div className="input-with-icon">
              <Scale size={18} className="input-icon" />
              <input
                type="text"
                className="modern-input"
                placeholder="Weight (e.g., 2 lbs)"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
            </div>
          </div>

          {/* Location & Date Section */}
          <div className="form-section">
            <div className="form-section-header">
              <MapPin size={20} />
              <span>Location & Date</span>
            </div>

            {/* Location Dropdown */}
            <div className="input-with-icon">
              <MapPin size={18} className="input-icon" />
              <select
                className="modern-select"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                <option value="">Select Stream</option>
                {streams.slice(0, 50).map(stream => (
                  <option key={stream.id} value={stream.name}>
                    {stream.name} - {stream.county}
                  </option>
                ))}
              </select>
            </div>

            <div className="divider-text">OR</div>

            {/* Custom Location */}
            <div className="input-with-icon">
              <MapPin size={18} className="input-icon" />
              <input
                type="text"
                className="modern-input"
                placeholder="Enter custom location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            {/* Date */}
            <div className="input-with-icon">
              <Calendar size={18} className="input-icon" />
              <input
                type="date"
                className="modern-input"
                value={dateCaught}
                onChange={(e) => setDateCaught(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {submitError && (
            <div className="form-error" role="alert" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', color: 'var(--error-red, #dc2626)', fontSize: '0.9rem' }}>
              {submitError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="modal-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-small"></div>
                  Posting...
                </>
              ) : (
                <>
                  <Fish size={18} />
                  Post Catch
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostCatchModal;